import { useEffect, useState } from 'react';

import { cn } from '@codes/ui-kit/utils';
import { Check, Pencil, X } from 'lucide-react';

import { Button, Input, Label, SharedSpinner } from '@codes/ui-kit';

interface EditableFieldProps {
    label: string;
    value: string;
    onSave: (value: string) => Promise<void>;
    disabled?: boolean;
    placeholder?: string;
}

export const EditableField = ({ label, value: initialValue, onSave, disabled, placeholder }: EditableFieldProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(initialValue);
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        if (!value.trim() || value === initialValue) {
            setIsEditing(false);
            return;
        }

        setIsSaving(true);
        try {
            await onSave(value.trim());
            setIsEditing(false);
        } catch (error) {
            setValue(initialValue);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setValue(initialValue);
        setIsEditing(false);
    };

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    return (
        <div className="space-y-2">
            <Label className="text-sm font-medium text-sub-title">{label}</Label>
            {isEditing ? (
                <div className="flex gap-2 items-center">
                    <Input
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        placeholder={placeholder}
                        className="flex-1"
                        autoFocus
                        disabled={isSaving}
                    />
                    <Button size="sm" onClick={handleSave} disabled={!value.trim() || isSaving}>
                        {isSaving ? <SharedSpinner className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleCancel} disabled={isSaving}>
                        <X className="w-4 h-4" />
                    </Button>
                </div>
            ) : (
                <div
                    className={cn(
                        'group flex items-center gap-2 px-3 py-2 rounded-md border border-border',
                        !disabled &&
                            'cursor-pointer hover:border-border hover:bg-box dark:hover:bg-hover/50 transition-all'
                    )}
                    onClick={() => {
                        if (!disabled) {
                            setIsEditing(true);
                        }
                    }}
                >
                    <span className="flex-1 text-sm md:text-base text-text truncate">{value || placeholder}</span>
                    {!disabled && (
                        <Pencil className="w-4 h-4 text-text-400 dark:text-text-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                </div>
            )}
        </div>
    );
};
